/**
 * Function Reference Dropdown
 *
 * "fx" button that opens a fixed-position dropdown with searchable, categorized
 * function list. Uses position:fixed to escape overflow containers in the Dialog.
 * Works because the Dialog uses inset-0 centering (no CSS transform).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { FunctionSquare, Search } from 'lucide-react';
import {
  Button,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@loader/ui';

import type { OperatorDefinition } from './operatorConstants';

/** Dropdown positioning constants */
const DROPDOWN_WIDTH_PX = 288; // matches w-72 (18rem)
const VIEWPORT_PADDING_PX = 8;
const DROPDOWN_GAP_PX = 4;

interface FunctionReferencePanelProps {
  operators: OperatorDefinition[];
  onInsert: (text: string) => void;
}

/** Category display order */
const CATEGORY_ORDER: { key: string; label: string }[] = [
  { key: 'math', label: 'Math' },
  { key: 'text', label: 'Text' },
  { key: 'date', label: 'Date' },
  { key: 'test', label: 'Test' },
  { key: 'logical', label: 'Logic' },
];

export function FunctionReferencePanel({ operators, onInsert }: FunctionReferencePanelProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Position the dropdown below the button using fixed coordinates
  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    let left = rect.right - DROPDOWN_WIDTH_PX;
    if (left < VIEWPORT_PADDING_PX) left = VIEWPORT_PADDING_PX;
    if (left + DROPDOWN_WIDTH_PX > window.innerWidth - VIEWPORT_PADDING_PX) {
      left = window.innerWidth - DROPDOWN_WIDTH_PX - VIEWPORT_PADDING_PX;
    }

    setPosition({ top: rect.bottom + DROPDOWN_GAP_PX, left });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Focus search when opened
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Filter to function-style operators (uppercase names), then by search query
  const filtered = useMemo(() => {
    const functions = operators.filter((op) => /^[A-Z]/.test(op.name));
    if (!search.trim()) return functions;

    const query = search.trim().toLowerCase();
    return functions.filter(
      (op) =>
        op.name.toLowerCase().includes(query) ||
        op.label.toLowerCase().includes(query) ||
        op.category.toLowerCase().includes(query)
    );
  }, [operators, search]);

  // Group filtered results by category
  const grouped = useMemo(() => {
    const map = new Map<string, OperatorDefinition[]>();
    for (const func of filtered) {
      const list = map.get(func.category) ?? [];
      list.push(func);
      map.set(func.category, list);
    }
    return map;
  }, [filtered]);

  const handleInsert = (funcName: string) => {
    onInsert(`${funcName}()`);
    setOpen(false);
    setSearch('');
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={buttonRef}
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            onClick={() => { setOpen(!open); if (open) setSearch(''); }}
          >
            <FunctionSquare className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">Insert function</TooltipContent>
      </Tooltip>

      {open && position && (
        <div
          ref={dropdownRef}
          className="fixed z-[9999] w-72 rounded-md border bg-popover text-popover-foreground shadow-md"
          style={{ top: position.top, left: position.left }}
        >
          {/* Search */}
          <div className="relative border-b px-3 py-2">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={searchRef}
              placeholder="Search functions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 pl-7 text-xs bg-transparent border-none shadow-none focus-visible:ring-0"
            />
          </div>

          {/* Function list */}
          <div className="max-h-64 overflow-y-auto p-2">
            {filtered.length === 0 ? (
              <p className="text-muted-foreground text-xs text-center py-4">
                No functions matching &ldquo;{search.trim()}&rdquo;
              </p>
            ) : (
              <div className="space-y-2">
                {CATEGORY_ORDER.map(({ key, label }) => {
                  const funcs = grouped.get(key);
                  if (!funcs?.length) return null;

                  return (
                    <div key={key}>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 px-1 mb-1">
                        {label}
                      </p>
                      <div className="space-y-0.5">
                        {funcs.map((func) => (
                          <button
                            key={func.name}
                            type="button"
                            onClick={() => handleInsert(func.name)}
                            className={cn(
                              'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left',
                              'hover:bg-muted/80 transition-colors cursor-pointer',
                            )}
                          >
                            <span className="font-mono text-xs font-medium shrink-0 mt-px">
                              {func.name}
                            </span>
                            <span className="text-[11px] text-muted-foreground leading-tight truncate">
                              {func.description || func.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
